import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { VideoPlayer } from '../VideoPlayer';
import { BaselineReviewOverlay } from '../BaselineReviewOverlay';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useUser } from '@/store/useAuthStore';
import { features } from '@/config/features';

/**
 * Step 4: Stir Swab (10-Second Countdown)
 * 
 * Instruct user to stir swab in solution for 10 seconds.
 * Countdown timer prevents user from proceeding too quickly.
 * 
 * GATE: Baseline measurement MUST be complete before proceeding to Step 5.
 * Button styling: 
 * - Grey if baseline NOT complete
 * - Blue if countdown complete AND baseline complete
 */
export function Step4StirSwab() {
  const setCurrentStep = useMeasurementWizardStore((s) => s.setCurrentStep);
  const baselineCompletedAt = useMeasurementWizardStore((s) => s.baselineCompletedAt);
  const baselineResult = useMeasurementWizardStore((s) => s.baselineResult);
  const testId = useMeasurementWizardStore((s) => s.testId);
  const sampleCode = useMeasurementWizardStore((s) => s.sampleCode);
  const user = useUser();
  const [countdown, setCountdown] = useState(10);
  const [showReview, setShowReview] = useState(false);

  const handleNext = () => {
    // Only proceed if both baseline and countdown are complete
    if (!isBaselineComplete || !isCountdownComplete) {
      return;
    }
    setCurrentStep(5);
  };

  useEffect(() => {
    if (countdown <= 0) return;
    
    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [countdown]);

  const isCountdownComplete = countdown <= 0;
  const isBaselineComplete = baselineCompletedAt !== null;

  return (
    <>
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 0 }}>
      <Typography variant="h5" gutterBottom align="center">
        Upper Respiratory Infection Panel
      </Typography>

      <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <VideoPlayer
          src="/videos/step-4-stir-swab.mp4"
          poster="/videos/step-4-poster.jpg"
          controls
          autoPlay
          loopCount={2}
        />
      </Box>

      <Typography variant="body1" sx={{ mb: 2 }}>
        Remove the foil from the plastic tube and insert the end 
        of the swab into the tube so it&apos;s fully submerged in the solution.
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        Stir the swab vigorously in the solution for 30 seconds.
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        While removing the swab from the tube, gently press the sides of the tube 
        to ensure the swab is properly drained.
      </Typography>

      {/* Button styling: Grey if baseline NOT complete or countdown NOT complete, Blue if both conditions met */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleNext}
        sx={{
          backgroundColor: (!isBaselineComplete || !isCountdownComplete) ? '#D3D3D3' : '#1976d2',
          color: (!isBaselineComplete || !isCountdownComplete) ? '#666666' : '#ffffff',
          '&:hover': {
            backgroundColor: (!isBaselineComplete || !isCountdownComplete) ? '#D3D3D3' : '#1565c0',
          },
        }}
      >
        {isCountdownComplete ? 'Next' : `${countdown}s`}
      </Button>

      {/* Baseline review — Admin/Researcher only, visible after baseline completes */}
      {isBaselineComplete &&
        baselineResult !== null &&
        features.enableResearchFeatures &&
        (user?.roles?.includes('researcher') || user?.roles?.includes('admin')) && (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => setShowReview(true)}
            sx={{ mt: 1 }}
          >
            Review Baseline
          </Button>
        )}
    </Box>

    {/* Baseline Review full-screen overlay */}
    {features.enableResearchFeatures && baselineResult !== null && baselineCompletedAt !== null && (
      <BaselineReviewOverlay
        open={showReview}
        testId={testId}
        sampleCode={sampleCode}
        baselineResult={baselineResult}
        baselineCompletedAt={baselineCompletedAt}
        onProceed={() => setShowReview(false)}
        onDiscard={() => setShowReview(false)}
      />
    )}
    </>
  );
}
